import type { SystemStyleObject } from "@chakra-ui/react";

import type { WidgetStyleCategory } from "@/types";

/** Appearance Editor → Project & Tool Pages colors on `layout.colorMapping`. */
export interface AppearanceColors {
  breadcrumbLinkColor: string;
  linkColor: string;
  pageBackground?: string;
  toolTabTextColor?: string;
  toolTabActiveTextColor?: string;
  toolTabBackgroundColorActive?: string;
  toolTitleTextColor?: string;
  widgetTitleTextColor?: string;
  widgetIconColor?: string;
  widgetCardBackground?: string;
  widgetBackgroundColor?: string;
  widgetBodyTextColor?: string;
  widgetMetaTextColor?: string;
  toolCardBackground?: string;
  toolBodyTextColor?: string;
  toolMetaTextColor?: string;
  tabBackgroundColor?: string;
}

const BREADCRUMB_KEYS = [
  "breadcrumbLinkColor",
  "breadcrumb_link_color",
  "breadcrumb-link-color"
];
const LINK_KEYS = ["linkColor", "link_color", "link-color"];
const PAGE_BACKGROUND_KEYS = [
  "backgroundColor",
  "background_color",
  "pageBackground",
  "page_background",
  "page-background"
];
const TOOL_TAB_TEXT_KEYS = [
  "toolTabTextColor",
  "tool_tab_text_color",
  "tool-tab-text-color"
];
const TOOL_TAB_ACTIVE_TEXT_KEYS = [
  "toolTabActiveTextColor",
  "tool_tab_active_text_color",
  "tool-tab-active-text-color"
];
const TOOL_TAB_ACTIVE_BG_KEYS = [
  "toolTabBackgroundColorActive",
  "tool_tab_background_color_active",
  "tool-tab-background-color-active"
];
const TOOL_TITLE_KEYS = [
  "toolTitleTextColor",
  "tool_title_text_color",
  "tool-title-text-color"
];
const WIDGET_TITLE_KEYS = [
  "widgetTitleTextColor",
  "widget_title_text_color",
  "widget-title-text-color"
];
const WIDGET_ICON_KEYS = [
  "widgetIconColor",
  "widget_icon_color",
  "widget-icon-color"
];
const WIDGET_CARD_BG_KEYS = [
  "widgetCardBackgroundColor",
  "widget_card_background_color",
  "widget-card-background-color"
];
const WIDGET_COLUMN_BG_KEYS = [
  "widgetBackgroundColor",
  "widget_background_color",
  "widget-background-color"
];
const WIDGET_BODY_TEXT_KEYS = [
  "widgetBodyTextColor",
  "widget_body_text_color",
  "widget-body-text-color"
];
const WIDGET_SECONDARY_TEXT_KEYS = [
  "widgetMetaTextColor",
  "widget_meta_text_color",
  "widget-meta-text-color"
];
const TOOL_CARD_BG_KEYS = [
  "toolCardBackgroundColor",
  "tool_card_background_color",
  "tool-card-background-color"
];
const TOOL_BODY_TEXT_KEYS = [
  "toolBodyTextColor",
  "tool_body_text_color",
  "tool-body-text-color"
];
const TOOL_SECONDARY_TEXT_KEYS = [
  "toolMetaTextColor",
  "tool_meta_text_color",
  "tool-meta-text-color"
];
const TAB_CARD_BG_KEYS = [
  "tabBackgroundColor",
  "tab_background_color",
  "tab-background-color"
];

const mappingValue = (
  colorMapping: Record<string, string> | null | undefined,
  keys: string[]
): string | undefined => {
  if (!colorMapping) return undefined;

  for (const key of keys) {
    const value = colorMapping[key];
    if (typeof value === "string" && value) return value;
  }

  return undefined;
};

const resolveColor = (
  value: string | undefined,
  fallback: string,
  brand: string
): string => {
  if (!value) return fallback;
  if (value === "$brand-color") return brand;
  return value;
};

const resolveOptionalColor = (
  value: string | undefined,
  fallback: string | undefined,
  brand: string
): string | undefined => {
  if (!value) return fallback;
  if (value === "$brand-color") return brand;
  return value;
};

export const getThemeAppearanceDefaults = (
  themeId: WidgetStyleCategory,
  brand: string
): AppearanceColors => {
  if (themeId === "fresh") {
    return {
      breadcrumbLinkColor: brand,
      linkColor: brand,
      pageBackground: "#fafafa",
      toolTabTextColor: "#8c8c8c",
      toolTabActiveTextColor: "#fff",
      toolTabBackgroundColorActive: brand,
      toolTitleTextColor: brand,
      widgetTitleTextColor: "#434343",
      widgetIconColor: brand
    };
  }

  if (themeId === "bold") {
    return {
      breadcrumbLinkColor: brand,
      linkColor: brand,
      pageBackground: "#ffffff",
      toolTabTextColor: "#fff",
      toolTabActiveTextColor: "#fff",
      toolTabBackgroundColorActive: brand,
      toolTitleTextColor: brand,
      widgetTitleTextColor: "#434343",
      widgetIconColor: brand
    };
  }

  return {
    breadcrumbLinkColor: brand,
    linkColor: brand,
    widgetIconColor: brand
  };
};

const assignOptional = (
  colors: AppearanceColors,
  key: keyof AppearanceColors,
  value: string | undefined
): void => {
  if (value) colors[key] = value;
};

/** Reads editor colors with per-theme defaults. Other pages can reuse this. */
export const resolveAppearanceColors = (
  colorMapping: Record<string, string> | null | undefined,
  defaults: AppearanceColors,
  brand: string
): AppearanceColors => {
  const colors: AppearanceColors = {
    breadcrumbLinkColor: resolveColor(
      mappingValue(colorMapping, BREADCRUMB_KEYS),
      defaults.breadcrumbLinkColor,
      brand
    ),
    linkColor: resolveColor(
      mappingValue(colorMapping, LINK_KEYS),
      defaults.linkColor,
      brand
    )
  };

  assignOptional(
    colors,
    "pageBackground",
    resolveOptionalColor(
      mappingValue(colorMapping, PAGE_BACKGROUND_KEYS),
      defaults.pageBackground,
      brand
    )
  );
  assignOptional(
    colors,
    "toolTabTextColor",
    resolveOptionalColor(
      mappingValue(colorMapping, TOOL_TAB_TEXT_KEYS),
      defaults.toolTabTextColor,
      brand
    )
  );
  assignOptional(
    colors,
    "toolTabActiveTextColor",
    resolveOptionalColor(
      mappingValue(colorMapping, TOOL_TAB_ACTIVE_TEXT_KEYS),
      defaults.toolTabActiveTextColor,
      brand
    )
  );
  assignOptional(
    colors,
    "toolTabBackgroundColorActive",
    resolveOptionalColor(
      mappingValue(colorMapping, TOOL_TAB_ACTIVE_BG_KEYS),
      defaults.toolTabBackgroundColorActive,
      brand
    )
  );
  assignOptional(
    colors,
    "toolTitleTextColor",
    resolveOptionalColor(
      mappingValue(colorMapping, TOOL_TITLE_KEYS),
      defaults.toolTitleTextColor,
      brand
    )
  );
  assignOptional(
    colors,
    "widgetTitleTextColor",
    resolveOptionalColor(
      mappingValue(colorMapping, WIDGET_TITLE_KEYS),
      defaults.widgetTitleTextColor,
      brand
    )
  );
  assignOptional(
    colors,
    "widgetIconColor",
    resolveOptionalColor(
      mappingValue(colorMapping, WIDGET_ICON_KEYS),
      defaults.widgetIconColor,
      brand
    )
  );
  assignOptional(
    colors,
    "widgetCardBackground",
    resolveOptionalColor(
      mappingValue(colorMapping, WIDGET_CARD_BG_KEYS),
      defaults.widgetCardBackground,
      brand
    )
  );
  assignOptional(
    colors,
    "widgetBackgroundColor",
    resolveOptionalColor(
      mappingValue(colorMapping, WIDGET_COLUMN_BG_KEYS),
      defaults.widgetBackgroundColor,
      brand
    )
  );
  assignOptional(
    colors,
    "widgetBodyTextColor",
    resolveOptionalColor(
      mappingValue(colorMapping, WIDGET_BODY_TEXT_KEYS),
      defaults.widgetBodyTextColor,
      brand
    )
  );
  assignOptional(
    colors,
    "widgetMetaTextColor",
    resolveOptionalColor(
      mappingValue(colorMapping, WIDGET_SECONDARY_TEXT_KEYS),
      defaults.widgetMetaTextColor,
      brand
    )
  );
  assignOptional(
    colors,
    "toolCardBackground",
    resolveOptionalColor(
      mappingValue(colorMapping, TOOL_CARD_BG_KEYS),
      defaults.toolCardBackground,
      brand
    )
  );
  assignOptional(
    colors,
    "toolBodyTextColor",
    resolveOptionalColor(
      mappingValue(colorMapping, TOOL_BODY_TEXT_KEYS),
      defaults.toolBodyTextColor,
      brand
    )
  );
  assignOptional(
    colors,
    "toolMetaTextColor",
    resolveOptionalColor(
      mappingValue(colorMapping, TOOL_SECONDARY_TEXT_KEYS),
      defaults.toolMetaTextColor,
      brand
    )
  );
  assignOptional(
    colors,
    "tabBackgroundColor",
    resolveOptionalColor(
      mappingValue(colorMapping, TAB_CARD_BG_KEYS),
      defaults.tabBackgroundColor,
      brand
    )
  );

  return colors;
};

const linkUnderlineSx: SystemStyleObject = {
  textDecoration: "underline",
  textUnderlineOffset: "2px"
};

export const appearanceLinkSx = (color: string): SystemStyleObject => ({
  color,
  ...linkUnderlineSx,
  _visited: { color },
  _hover: { color, ...linkUnderlineSx }
});

/**
 * Links inside an alert use the alert text color. `inherit` follows that text,
 * and the extra `:not()`s beat the page brand-link rule.
 */
export const appearanceAlertLinkSx = (): SystemStyleObject =>
  appearanceLinkSx("inherit");

export const APPEARANCE_ALERT_LINK_SELECTOR =
  "& .chakra-alert__root a:not(.chakra-button):not(.chakra-breadcrumb__link), & .chakra-alert__root button, & .chakra-toast__root a:not(.chakra-button):not(.chakra-breadcrumb__link), & .chakra-toast__root button";

const buttonLinkSelector =
  "& a.chakra-button, & a:has(.chakra-button)" as const;

const breadcrumbLinkSelector = "& a.chakra-breadcrumb__link" as const;

const buttonLinkSx: SystemStyleObject = {
  textDecoration: "none",
  _hover: { textDecoration: "none" },
  _focus: { textDecoration: "none" }
};

/** Page-level bg + body links. Spread onto a layout/main for any future page. */
export const appearancePageSx = (
  colors: AppearanceColors
): SystemStyleObject => ({
  ...(colors.pageBackground ? { bg: colors.pageBackground } : {}),
  "& a:not(.chakra-button):not(.chakra-breadcrumb__link)": appearanceLinkSx(
    colors.linkColor
  ),
  [buttonLinkSelector]: buttonLinkSx,
  [breadcrumbLinkSelector]: {
    textDecoration: "none",
    textUnderlineOffset: "2px",
    _hover: linkUnderlineSx
  },
  [APPEARANCE_ALERT_LINK_SELECTOR]: appearanceAlertLinkSx()
});

export const appearanceBreadcrumbSx = (
  colors: AppearanceColors
): SystemStyleObject => ({
  "& a": {
    color: colors.breadcrumbLinkColor,
    textDecoration: "none",
    textUnderlineOffset: "2px",
    _visited: { color: colors.breadcrumbLinkColor },
    _hover: { color: colors.breadcrumbLinkColor, ...linkUnderlineSx }
  }
});
